<template>
  <div class="test-review-tab">
    <div class="tab-header mb-6">
      <h3 class="text-lg font-semibold text-gray-900">Test & Review</h3>
      <p class="text-sm text-gray-600 mt-1">
        Review your configuration and test the connection before saving
      </p>
    </div>

    <!-- Configuration Summary -->
    <div class="configuration-summary mb-6">
      <h4 class="text-md font-medium text-gray-800 mb-4 flex items-center space-x-2">
        <i class="fas fa-clipboard-check text-blue-500"></i>
        <span>Configuration Summary</span>
      </h4>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Basic Configuration -->
        <div class="summary-card">
          <h5 class="font-medium text-gray-700 mb-3">Basic Setup</h5>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Foundation:</span>
              <span class="font-medium">{{ getFoundationName() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Status:</span>
              <span :class="formData.enabled ? 'text-green-600' : 'text-gray-500'" class="font-medium">
                {{ formData.enabled ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
            <div v-if="formData.template_id" class="flex justify-between">
              <span class="text-gray-600">Template:</span>
              <span class="font-medium">{{ getTemplateName() }}</span>
            </div>
          </div>
        </div>

        <!-- Connection Settings -->
        <div class="summary-card">
          <h5 class="font-medium text-gray-700 mb-3">Connection Settings</h5>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Sync Mode:</span>
              <span class="font-medium">{{ getSyncModeText() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Duplicate Check:</span>
              <span class="font-medium">{{ getDuplicateCheckText() }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Assignment Rules:</span>
              <span :class="formData.connection_settings?.use_assignment_rules ? 'text-green-600' : 'text-gray-500'" class="font-medium">
                {{ formData.connection_settings?.use_assignment_rules ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
          </div>
        </div>

        <!-- Sync Settings -->
        <div class="summary-card">
          <h5 class="font-medium text-gray-700 mb-3">Sync Settings</h5>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Batch Size:</span>
              <span class="font-medium">{{ formData.sync_settings?.batch_size || 50 }} records</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Retry Attempts:</span>
              <span class="font-medium">{{ formData.sync_settings?.retry_attempts ?? 3 }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Log Level:</span>
              <span class="font-medium">{{ formData.sync_settings?.log_level || 'Info' }}</span>
            </div>
          </div>
        </div>

        <!-- Field Mappings -->
        <div class="summary-card">
          <h5 class="font-medium text-gray-700 mb-3">Field Mappings</h5>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Total Mappings:</span>
              <span class="font-medium">{{ totalMappings }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Completed:</span>
              <span class="font-medium text-green-600">{{ completedMappings }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Target Object:</span>
              <span class="font-medium">{{ getTargetObjectText() }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Field Mappings Preview -->
    <div v-if="completedMappings > 0" class="mappings-preview mb-6">
      <h4 class="text-md font-medium text-gray-800 mb-4 flex items-center space-x-2">
        <i class="fas fa-table text-purple-500"></i>
        <span>Field Mappings Preview</span>
      </h4>
      
      <div class="bg-white border rounded-lg overflow-hidden">
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Source
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Salesforce Field
                </th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="mapping in previewMappings" :key="mapping.id" class="hover:bg-gray-50">
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                        :class="getMappingTypeClass(mapping.mapping_type)">
                    {{ getMappingTypeText(mapping.mapping_type) }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ getSourceText(mapping) }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {{ mapping.target_field }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <span v-if="isValidMapping(mapping)" class="inline-flex items-center text-green-600">
                    <i class="fas fa-check-circle mr-1"></i>
                    Valid
                  </span>
                  <span v-else class="inline-flex items-center text-red-600">
                    <i class="fas fa-exclamation-circle mr-1"></i>
                    Incomplete
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Validation Results -->
    <div class="validation-results mb-6">
      <h4 class="text-md font-medium text-gray-800 mb-4 flex items-center space-x-2">
        <i class="fas fa-clipboard-check text-green-500"></i>
        <span>Configuration Validation</span>
      </h4>
      
      <div class="space-y-3">
        <!-- Required Fields Check -->
        <div class="validation-item">
          <div class="flex items-center space-x-3">
            <i :class="hasFoundation ? 'fas fa-check-circle text-green-500' : 'fas fa-times-circle text-red-500'"></i>
            <span class="text-sm font-medium">Foundation Campaign Selected</span>
          </div>
          <p v-if="!hasFoundation" class="text-xs text-red-600 ml-6">
            Please select a foundation campaign in the Basic Setup tab
          </p>
        </div>

        <div class="validation-item">
          <div class="flex items-center space-x-3">
            <i :class="hasValidMappings ? 'fas fa-check-circle text-green-500' : 'fas fa-times-circle text-red-500'"></i>
            <span class="text-sm font-medium">Field Mappings Configured</span>
          </div>
          <p v-if="!hasValidMappings" class="text-xs text-red-600 ml-6">
            Please configure at least one complete field mapping
          </p>
        </div>

        <div class="validation-item">
          <div class="flex items-center space-x-3">
            <i :class="hasRequiredMappings ? 'fas fa-check-circle text-green-500' : 'fas fa-exclamation-triangle text-yellow-500'"></i>
            <span class="text-sm font-medium">Required Salesforce Fields</span>
          </div>
          <p v-if="!hasRequiredMappings" class="text-xs text-yellow-600 ml-6">
            Consider mapping required fields like LastName and Email for better success rates
          </p>
        </div>

        <div class="validation-item">
          <div class="flex items-center space-x-3">
            <i class="fas fa-info-circle text-blue-500"></i>
            <span class="text-sm font-medium">Configuration Status</span>
          </div>
          <p class="text-xs text-gray-600 ml-6">
            {{ getConfigurationStatus() }}
          </p>
        </div>
      </div>
    </div>

    <!-- Connection Test -->
    <div class="connection-test mb-6">
      <h4 class="text-md font-medium text-gray-800 mb-4 flex items-center space-x-2">
        <i class="fas fa-plug text-blue-500"></i>
        <span>Connection Test</span>
      </h4>
      
      <div class="bg-gray-50 rounded-lg p-4">
        <div class="flex items-center justify-between mb-3">
          <div>
            <p class="text-sm font-medium text-gray-700">Test Salesforce Connection</p>
            <p class="text-xs text-gray-500">Verify that your configuration can connect to Salesforce</p>
          </div>
          <button
            @click="testConnection"
            :disabled="!canTest || testing"
            class="btn btn-primary"
          >
            <i v-if="testing" class="fas fa-spinner fa-spin mr-2"></i>
            <i v-else class="fas fa-plug mr-2"></i>
            {{ testing ? 'Testing...' : 'Test Connection' }}
          </button>
        </div>

        <!-- Test Results -->
        <div v-if="testResult" class="mt-4 p-3 rounded-md" :class="testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'">
          <div class="flex items-start space-x-2">
            <i :class="testResult.success ? 'fas fa-check-circle text-green-500' : 'fas fa-times-circle text-red-500'" class="mt-0.5"></i>
            <div class="flex-1">
              <p class="text-sm font-medium" :class="testResult.success ? 'text-green-800' : 'text-red-800'">
                {{ testResult.success ? 'Connection Successful' : 'Connection Failed' }}
              </p>
              <p class="text-xs mt-1" :class="testResult.success ? 'text-green-600' : 'text-red-600'">
                {{ testResult.message }}
              </p>
              
              <!-- Success Details -->
              <div v-if="testResult.success && testResult.details" class="mt-2 text-xs space-y-1">
                <div v-if="testResult.details.instance_url" class="text-green-600">
                  <span class="font-medium">Instance URL:</span> {{ testResult.details.instance_url }}
                </div>
                <div v-if="testResult.details.api_version" class="text-green-600">
                  <span class="font-medium">API Version:</span> {{ testResult.details.api_version }}
                </div>
              </div>
              
              <!-- Error Details -->
              <div v-if="!testResult.success && testResult.errors" class="mt-2">
                <p class="text-xs font-medium text-red-700">Errors:</p>
                <ul class="mt-1 text-xs text-red-600 list-disc list-inside">
                  <li v-for="error in testResult.errors" :key="error">{{ error }}</li>
                </ul>
              </div>
              
              <!-- Troubleshooting Tips -->
              <div v-if="!testResult.success && testResult.troubleshooting" class="mt-3">
                <p class="text-xs font-medium text-red-700">Troubleshooting Tips:</p>
                <ul class="mt-1 text-xs text-red-600 space-y-1">
                  <li v-for="tip in testResult.troubleshooting" :key="tip" class="flex items-start">
                    <i class="fas fa-info-circle text-red-400 mr-1 mt-0.5 flex-shrink-0"></i>
                    <span>{{ tip }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!canTest" class="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div class="flex items-start space-x-2">
            <i class="fas fa-exclamation-triangle text-yellow-500 mt-0.5"></i>
            <div>
              <p class="text-sm font-medium text-yellow-800">Cannot Test Connection</p>
              <p class="text-xs text-yellow-600 mt-1">
                Please complete the required configuration fields before testing
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Save Actions -->
    <div class="save-actions">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div class="flex items-start space-x-3">
          <i class="fas fa-info-circle text-blue-500 mt-1"></i>
          <div class="flex-1">
            <h4 class="text-sm font-medium text-blue-800 mb-2">Ready to Save?</h4>
            <p class="text-xs text-blue-600 mb-3">
              Your configuration {{ canSave ? 'is ready to be saved' : 'needs to be completed before saving' }}.
              {{ formData.enabled ? 'Sync will begin immediately after saving.' : 'You can enable sync later in the configuration.' }}
            </p>
            
            <div class="flex items-center space-x-3">
              <button
                @click="$emit('cancel')"
                class="btn btn-outline"
              >
                Cancel
              </button>
              
              <button
                @click="$emit('save-configuration')"
                :disabled="!canSave"
                class="btn btn-primary"
              >
                <i class="fas fa-save mr-2"></i>
                {{ configuration ? 'Update' : 'Create' }} Configuration
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import axios from 'axios';

export default {
  name: 'TestReviewTab',
  props: {
    configuration: {
      type: Object,
      default: null,
    },
    formData: {
      type: Object,
      required: true,
    },
  },
  emits: ['test-connection', 'save-configuration', 'cancel'],
  setup(props, { emit }) {
    const testing = ref(false);
    const testResult = ref(null);

    // Computed properties
    const totalMappings = computed(() => {
      return props.formData.field_mappings?.length || 0;
    });

    const completedMappings = computed(() => {
      return props.formData.field_mappings?.filter(mapping => 
        mapping.target_field && (mapping.source_field || mapping.mapping_value)
      ).length || 0;
    });

    const previewMappings = computed(() => {
      return props.formData.field_mappings?.filter(mapping => 
        mapping.target_field && (mapping.source_field || mapping.mapping_value)
      ).slice(0, 10) || []; // Show first 10 completed mappings
    });

    const hasFoundation = computed(() => {
      return !!props.formData.foundation_campaign_id;
    });

    const hasValidMappings = computed(() => {
      return completedMappings.value > 0;
    });

    const hasRequiredMappings = computed(() => {
      const mappings = props.formData.field_mappings || [];
      const hasLastName = mappings.some(m => m.target_field === 'LastName');
      const hasEmail = mappings.some(m => m.target_field === 'Email');
      return hasLastName || hasEmail;
    });

    const canTest = computed(() => {
      return hasFoundation.value && hasValidMappings.value;
    });

    const canSave = computed(() => {
      return hasFoundation.value && hasValidMappings.value;
    });

    // Methods
    const getFoundationName = () => {
      // This would normally come from a prop or API call
      return props.formData.foundation_campaign_id ? 'Selected Foundation' : 'None';
    };

    const getTemplateName = () => {
      return props.formData.template_id ? 'Template Applied' : '';
    };

    const getSyncModeText = () => {
      const mode = props.formData.connection_settings?.sync_mode || 'create_update';
      return mode === 'create_only' ? 'Create Only' : 'Create or Update';
    };

    const getDuplicateCheckText = () => {
      const field = props.formData.connection_settings?.duplicate_check_field || 'email';
      const labels = {
        email: 'Email Address',
        email_and_name: 'Email + Name',
        custom: 'Custom Field',
      };
      return labels[field] || 'Email Address';
    };

    const getTargetObjectText = () => {
      const mappings = props.formData.field_mappings || [];
      if (mappings.length > 0) {
        return mappings[0].target_object || 'Lead';
      }
      return 'Lead';
    };

    const getMappingTypeText = (type) => {
      const labels = {
        direct: 'Direct',
        hardcoded: 'Static',
        computed: 'Computed',
      };
      return labels[type] || type;
    };

    const getMappingTypeClass = (type) => {
      const classes = {
        direct: 'bg-blue-100 text-blue-800',
        hardcoded: 'bg-purple-100 text-purple-800',
        computed: 'bg-green-100 text-green-800',
      };
      return classes[type] || 'bg-gray-100 text-gray-800';
    };

    const getSourceText = (mapping) => {
      if (mapping.mapping_type === 'direct') {
        return mapping.source_field || 'Not set';
      } else if (mapping.mapping_type === 'hardcoded') {
        return `"${mapping.mapping_value || 'Not set'}"`;
      } else if (mapping.mapping_type === 'computed') {
        return mapping.mapping_value || 'Not set';
      }
      return 'Not set';
    };

    const isValidMapping = (mapping) => {
      return mapping.target_field && (mapping.source_field || mapping.mapping_value);
    };

    const getConfigurationStatus = () => {
      if (!hasFoundation.value) {
        return 'Foundation campaign must be selected';
      } else if (!hasValidMappings.value) {
        return 'Field mappings must be configured';
      } else if (!hasRequiredMappings.value) {
        return 'Consider mapping required Salesforce fields';
      } else {
        return 'Configuration is complete and ready to save';
      }
    };

    const testConnection = async () => {
      testing.value = true;
      testResult.value = null;

      try {
        // Prepare request data
        const requestData = {};
        
        // Include configuration_id if we're editing an existing configuration
        if (props.configuration && props.configuration.id) {
          requestData.configuration_id = props.configuration.id;
        }

        // Make API call to test connection
        const response = await axios.post('/admin/salesforce/test-connection', requestData);
        
        if (response.data.success) {
          testResult.value = {
            success: true,
            message: response.data.message || 'Connection successful!',
            details: response.data.details || null,
          };
          
          // Also emit success event to parent
          emit('test-connection', { success: true, data: response.data });
        } else {
          testResult.value = {
            success: false,
            message: response.data.message || 'Connection test failed',
            errors: response.data.errors || null,
            troubleshooting: response.data.troubleshooting || null,
          };
        }
      } catch (error) {
        // Handle different error response formats
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          testResult.value = {
            success: false,
            message: error.response.data.message || 'Connection test failed',
            errors: error.response.data.errors || null,
            troubleshooting: error.response.data.troubleshooting || null,
            step: error.response.data.step || null,
          };
        } else if (error.request) {
          // The request was made but no response was received
          testResult.value = {
            success: false,
            message: 'No response from server. Please check your network connection.',
          };
        } else {
          // Something happened in setting up the request that triggered an Error
          testResult.value = {
            success: false,
            message: 'Connection test failed: ' + error.message,
          };
        }
        
        // Emit failure event to parent
        emit('test-connection', { success: false, error: error });
      } finally {
        testing.value = false;
      }
    };

    return {
      testing,
      testResult,
      totalMappings,
      completedMappings,
      previewMappings,
      hasFoundation,
      hasValidMappings,
      hasRequiredMappings,
      canTest,
      canSave,
      getFoundationName,
      getTemplateName,
      getSyncModeText,
      getDuplicateCheckText,
      getTargetObjectText,
      getMappingTypeText,
      getMappingTypeClass,
      getSourceText,
      isValidMapping,
      getConfigurationStatus,
      testConnection,
    };
  },
};
</script>

<style scoped>
.summary-card {
  @apply bg-gray-50 rounded-lg p-4 border;
}

.validation-item {
  @apply py-2;
}

.btn {
  @apply px-4 py-2 rounded font-medium transition-colors;
}

.btn-primary {
  @apply bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed;
}

.btn-outline {
  @apply border border-gray-300 text-gray-700 hover:bg-gray-50;
}

.tab-header {
  @apply border-b border-gray-200 pb-4;
}

@media (max-width: 768px) {
  .grid-cols-1.md\\:grid-cols-2 {
    @apply grid-cols-1;
  }
  
  .summary-card {
    @apply p-3;
  }
  
  .table {
    @apply text-xs;
  }
}
</style>